import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/cn";
import { getScanSources } from "@/api/explore";
import { enableMyCompanyScan } from "@/api/companies";
import { listProviders } from "@/api/providers";
import type { ExploreScanSource } from "@career-ops-amplify/shared";
import { CompanyLogo } from "@/components/companies/company-logo";
import { scannableAtsProviderIds } from "@/lib/provider-ui";
import { RECOMMENDED_FS_SCAN_SLUGS } from "@/lib/recommended-scan-employers";
import { DISCOVERY_QUERY_KEY } from "@/hooks/useDiscovery";

const ATS_LABELS: Record<string, string> = {
  greenhouse: "Greenhouse",
  ashby: "Ashby",
  lever: "Lever",
  smartrecruiters: "SmartRecruiters",
  rippling: "Rippling",
  workable: "Workable",
  workday: "Workday",
};

type ScopeFilter = "mine" | "scannable" | "all";

interface CompanyScanPickerProps {
  selectedSlugs: string[];
  onChange: (slugs: string[]) => void;
  initialSearch?: string;
  onSearchWeb?: (companyName: string) => void;
}

export function CompanyScanPicker({
  selectedSlugs,
  onChange,
  initialSearch = "",
  onSearchWeb,
}: CompanyScanPickerProps) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState(initialSearch);
  const [scope, setScope] = useState<ScopeFilter>("mine");
  const [atsFilter, setAtsFilter] = useState<string>("");
  const [enableMsg, setEnableMsg] = useState<string | null>(null);

  const scannableOnly = scope !== "all";

  const { data, isLoading } = useQuery({
    queryKey: ["explore", "sources", scannableOnly, search],
    queryFn: () =>
      getScanSources({
        scannableOnly,
        search: search.trim() || undefined,
      }),
    staleTime: 60_000,
  });

  const { data: providerList } = useQuery({
    queryKey: ["providers"],
    queryFn: async () => (await listProviders()).providers,
    staleTime: 60_000,
  });

  const enableRecommended = useMutation({
    mutationFn: async () => {
      const sources = data?.sources ?? [];
      const enabled: string[] = [];
      const skipped: string[] = [];
      for (const slug of RECOMMENDED_FS_SCAN_SLUGS) {
        const s = sources.find((x) => x.slug === slug);
        if (!s?.scannable || !s.ats || !s.boardToken) {
          skipped.push(slug);
          continue;
        }
        if (s.isTracked || s.source === "user") {
          enabled.push(slug);
          continue;
        }
        await enableMyCompanyScan(slug, {
          name: s.name,
          ats: s.ats,
          boardToken: s.boardToken,
          careersUrl: s.careersUrl,
        });
        enabled.push(slug);
      }
      return { enabled, skipped };
    },
    onSuccess: ({ enabled, skipped }) => {
      void queryClient.invalidateQueries({ queryKey: ["explore", "sources"] });
      void queryClient.invalidateQueries({ queryKey: DISCOVERY_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ["companies"] });
      onChange(Array.from(new Set([...selectedSlugs, ...enabled])));
      setScope("mine");
      setEnableMsg(
        skipped.length
          ? `Enabled ${enabled.length}; skipped ${skipped.join(", ")} (not in directory).`
          : `Enabled ${enabled.length} recommended finance/tech employers.`,
      );
    },
    onError: (err: unknown) => {
      setEnableMsg(err instanceof Error ? err.message : "Failed to enable employers");
    },
  });

  const atsFilterOptions = useMemo(
    () => scannableAtsProviderIds(providerList ?? []),
    [providerList],
  );

  const sources = useMemo(() => {
    let list = data?.sources ?? [];
    if (scope === "mine") {
      const mine = list.filter((s) => s.isTracked || s.source === "user");
      // Fall back to all scannable when user has not enabled anyone yet.
      list = mine.length > 0 ? mine : list.filter((s) => s.scannable);
    }
    if (atsFilter) {
      list = list.filter((s) => s.ats === atsFilter);
    }
    return list;
  }, [data?.sources, atsFilter, scope]);

  const selectedSet = useMemo(() => new Set(selectedSlugs), [selectedSlugs]);
  const mineCount = useMemo(
    () =>
      (data?.sources ?? []).filter((s) => s.isTracked || s.source === "user")
        .length,
    [data?.sources],
  );

  function toggle(slug: string) {
    if (selectedSet.has(slug)) {
      onChange(selectedSlugs.filter((s) => s !== slug));
    } else {
      onChange([...selectedSlugs, slug]);
    }
  }

  function selectScannableAll() {
    onChange(sources.filter((s) => s.scannable).map((s) => s.slug));
  }

  function clearAll() {
    onChange([]);
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          placeholder="Search employers…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="min-w-[200px] flex-1 rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
        />
        {(
          [
            { id: "mine" as const, label: "My enabled" },
            { id: "scannable" as const, label: "All scannable" },
            { id: "all" as const, label: "Show all" },
          ] as const
        ).map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => setScope(opt.id)}
            className={cn(
              "rounded-md border px-2 py-1 text-xs transition",
              scope === opt.id
                ? "border-brand bg-brand/10 text-brand"
                : "border-border text-muted-foreground hover:bg-muted/20",
            )}
          >
            {opt.label}
          </button>
        ))}
        <button
          type="button"
          onClick={selectScannableAll}
          className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted/20"
        >
          Select all visible
        </button>
        <button
          type="button"
          onClick={clearAll}
          className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted/20"
        >
          Clear
        </button>
        <button
          type="button"
          onClick={() => {
            setEnableMsg(null);
            enableRecommended.mutate();
          }}
          disabled={enableRecommended.isPending}
          className="rounded-md border border-brand/40 bg-brand/10 px-2 py-1 text-xs font-medium text-brand hover:bg-brand/20 disabled:opacity-50"
        >
          {enableRecommended.isPending
            ? "Enabling…"
            : "Enable recommended FS/tech"}
        </button>
      </div>
      {enableMsg ? (
        <p className="text-[11px] text-muted-foreground">{enableMsg}</p>
      ) : (
        <p className="text-[11px] text-muted-foreground">
          Recommended: {RECOMMENDED_FS_SCAN_SLUGS.join(", ")} — expands My
          enabled beyond IC-heavy defaults.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {(["", ...atsFilterOptions] as string[]).map((ats) => (
          <button
            key={ats || "all"}
            type="button"
            onClick={() => setAtsFilter(ats)}
            className={cn(
              "rounded-full border px-2.5 py-1 text-[11px] font-medium transition",
              atsFilter === ats
                ? "border-brand bg-brand/10 text-brand"
                : "border-border text-muted-foreground hover:bg-muted/20",
            )}
          >
            {ats ? (ATS_LABELS[ats] ?? ats) : "All ATS"}
          </button>
        ))}
      </div>

      <p className="text-xs text-muted-foreground">
        {selectedSlugs.length} selected
        {data ? ` · ${data.scannableCount} scannable in directory` : ""}
        {mineCount > 0 ? ` · ${mineCount} enabled for you` : ""}
        {scope === "mine" && mineCount === 0
          ? " · showing all scannable until you enable employers"
          : ""}
      </p>

      {isLoading && (
        <div className="flex justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted border-t-brand" />
        </div>
      )}

      {!isLoading && sources.length === 0 && (
        <p className="rounded-md border border-border bg-muted/10 px-3 py-4 text-sm text-muted-foreground">
          No employers match.{" "}
          <Link to="/companies" className="text-brand hover:underline">
            Browse the employer directory
          </Link>{" "}
          and use <strong>Enable scan</strong> on a profile, or switch to Search
          for web results.
        </p>
      )}

      <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border border-border p-1">
        {sources.map((source) => (
          <CompanyRow
            key={source.slug}
            source={source}
            selected={selectedSet.has(source.slug)}
            onToggle={() => toggle(source.slug)}
            onSearchWeb={
              !source.scannable && onSearchWeb
                ? () => onSearchWeb(source.name)
                : undefined
            }
          />
        ))}
      </div>
    </div>
  );
}

function CompanyRow({
  source,
  selected,
  onToggle,
  onSearchWeb,
}: {
  source: ExploreScanSource;
  selected: boolean;
  onToggle: () => void;
  onSearchWeb?: () => void;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-md px-2 py-2 transition hover:bg-muted/10",
        selected && "bg-brand/5",
        !source.scannable && "opacity-80",
      )}
    >
      <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          disabled={!source.scannable}
          className="rounded border-border"
        />
        <CompanyLogo name={source.name} logoUrl={source.logoUrl} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-foreground">
              {source.name}
            </span>
            {(source.isTracked || source.source === "user") && (
              <span className="rounded bg-brand/10 px-1.5 py-0.5 text-[10px] font-medium text-brand">
                Mine
              </span>
            )}
            {source.ats && (
              <span className="rounded bg-sky-500/10 px-1.5 py-0.5 text-[10px] font-medium text-sky-400">
                {ATS_LABELS[source.ats] ?? source.ats}
              </span>
            )}
            {!source.scannable && source.skipReason && (
              <span className="text-[10px] text-amber-600">
                {source.skipReason}
              </span>
            )}
          </div>
          {source.industry && (
            <p className="text-xs text-muted-foreground">{source.industry}</p>
          )}
        </div>
      </label>
      {onSearchWeb && (
        <button
          type="button"
          onClick={onSearchWeb}
          className="shrink-0 text-[11px] text-brand hover:underline"
        >
          Search web
        </button>
      )}
    </div>
  );
}
